/**
 * SiteCoreLinks.tsx — Wave 40
 *
 * Locale-aware, SSR-rendered contextual link block for top-level pages that
 * previously carried very few in-body outbound links (thin hubs).
 * It links to the eight core service hubs in the visitor's own language,
 * skips the page it is rendered on, and offers the mirror-language version
 * so crawlers can walk TH <-> EN without relying on the header nav alone.
 */
import { Link, useRouterState } from "@tanstack/react-router";

type Loc = "th" | "en";

const CORE = [
  { slug: "/translate", th: "แปลเอกสารรับรอง (ทุกภาษา)", en: "Certified document translation" },
  { slug: "/notary", th: "Notary Public รับรองลายมือชื่อ", en: "Notary Public / Notarial Services Attorney" },
  { slug: "/apostille", th: "Apostille และรับรองกงสุล (กต.)", en: "Apostille & MFA legalisation" },
  { slug: "/embassy", th: "รับรองเอกสารสถานทูต", en: "Embassy document legalisation" },
  { slug: "/visa", th: "บริการวีซ่าและเอกสารประกอบ", en: "Visa document services" },
  { slug: "/marriage", th: "จดทะเบียนสมรสกับชาวต่างชาติ", en: "Marriage registration abroad" },
  { slug: "/police-clearance", th: "ใบรับรองความประพฤติ (ตำรวจ)", en: "Police clearance certificate" },
  { slug: "/pricing", th: "ตารางราคาบริการทั้งหมด", en: "Full service price list" },
] as const;

const HEADING: Record<Loc, string> = {
  th: "บริการหลักที่เกี่ยวข้อง",
  en: "Related core services",
};

const MIRROR: Record<Loc, string> = {
  th: "Read this page in English",
  en: "อ่านหน้านี้เป็นภาษาไทย",
};

export default function SiteCoreLinks({ locale }: { locale?: Loc }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const loc: Loc = locale ?? (pathname.startsWith("/en") ? "en" : "th");
  const prefix = loc === "en" ? "/en" : "";
  const current = pathname.replace(/\/$/, "");

  const items = CORE.map((c) => ({
    to: `${prefix}${c.slug}`,
    label: loc === "en" ? c.en : c.th,
  })).filter((i) => i.to !== current);

  const mirror =
    loc === "en"
      ? current.replace(/^\/en/, "") || "/"
      : `/en${current === "/" ? "" : current}`;

  return (
    <section
      className="mx-auto max-w-6xl px-4 py-10 sm:px-6"
      aria-label={HEADING[loc]}
      data-track-zone="site-core-links"
    >
      <h2 className="font-display text-xl font-extrabold text-navy-deep">{HEADING[loc]}</h2>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((i) => (
          <li key={i.to}>
            <Link
              to={i.to as never}
              className="block rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:-translate-y-0.5 hover:border-amber-400 hover:text-amber-700"
            >
              {i.label}
            </Link>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-slate-500">
        <Link to={mirror as never} className="text-amber-700 underline-offset-2 hover:underline">
          {MIRROR[loc]}
        </Link>
      </p>
    </section>
  );
}
